// A drawer that moves without a CSS transition reports no end, so the pair
// needs a bound. 500ms clears the longest drawer transition.
const MOTION_DEADLINE_MS = 500

export interface DrawerMotionAnnouncer {
  // Announces a width change CSS will not report: `prefers-reduced-motion` and
  // `no_animations` drop the transition, and a drag writes the width directly.
  announceMotion: () => void
  unsubscribe: () => void
}

// Every drawer transition must announce exactly one start/end pair: consumers
// react per transition, and a missed pair stops them silently.
export default (drawer: HTMLElement): DrawerMotionAnnouncer => {
  let deadline: ReturnType<typeof setTimeout> | undefined
  // Whether a motion is outstanding. Both producers, the transition events and
  // an announced motion, gate on it, so neither can open a second pair over an
  // open one or close a pair that was never opened. One motion animating
  // several properties fires every transitionstart before any transitionend
  // (nothing here staggers a transition-delay), so the flag collapses the burst
  // on its own. A time window would additionally swallow a second motion that
  // legitimately followed close behind.
  let motionOpen = false

  const openMotion = (): void => {
    if (motionOpen) return
    motionOpen = true
    window.eventHub.$emit('drawer:animation-start')
  }

  const closeMotion = (): void => {
    if (!motionOpen) return
    motionOpen = false
    clearTimeout(deadline)
    deadline = undefined
    window.eventHub.$emit('drawer:animation-end')
  }

  // Transitions on descendants bubble; only the drawer's own motion counts.
  const onTransitionStart = (e: Event): void => {
    if (e.target === drawer) openMotion()
  }

  // A drag drops the transition mid-flight, which cancels rather than ends it.
  const onTransitionSettled = (e: Event): void => {
    if (e.target === drawer) closeMotion()
  }

  const announceMotion = (): void => {
    openMotion()
    // Re-arming extends the outstanding motion rather than opening a second.
    clearTimeout(deadline)
    deadline = setTimeout(closeMotion, MOTION_DEADLINE_MS)
  }

  drawer.addEventListener('transitionstart', onTransitionStart)
  drawer.addEventListener('transitionend', onTransitionSettled)
  drawer.addEventListener('transitioncancel', onTransitionSettled)

  return {
    announceMotion,
    unsubscribe: (): void => {
      // Closes an outstanding motion rather than stranding consumers on a
      // start whose end would have come from the deadline.
      closeMotion()
      drawer.removeEventListener('transitionstart', onTransitionStart)
      drawer.removeEventListener('transitionend', onTransitionSettled)
      drawer.removeEventListener('transitioncancel', onTransitionSettled)
    },
  }
}
